import { withUploadToast } from "../../lib/uploadToast";
import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Download,
  FileText,
  Loader2,
  MessageSquare,
  Upload,
  X,
} from "lucide-react";
import Button from "../../components/ui/Button";
import Spinner from "../../components/ui/Spinner";
import { useConversationPanel } from "../../hooks/useConversationPanel";
import { getErrorMessage } from "../../lib/apiClient";
import { formatBytes } from "../../utils/format";
import InlinePreview from "./components/InlinePreview";
import SharedConversationSidebar from "./components/SharedConversationSidebar";
import {
  downloadSharedFile,
  fetchSharedPreviewBlob,
  getSharedFileMeta,
  sharedKeys,
  uploadSharedFileVersion,
} from "./sharedAccessApi";

/**
 * Full-screen viewer for a file reached via an email-share grant — the
 * "shared with me" counterpart to FileViewerPage. Every capability (preview,
 * download, replace-content, chat/comments) is gated on the `actions` the
 * grant actually carries, resolved fresh from `GET shared/files/:id/meta` (no
 * caching delay, unlike workspace membership). No inline rename here, by
 * design — the owner's own FileViewerPage doesn't expose rename from the
 * full-screen toolbar either (it's a file-browser context-menu action), so
 * this mirrors that rather than adding a capability the owner's view lacks.
 */
export default function SharedFileViewerPage() {
  const { fileId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [conversationOpen, setConversationOpen] = useConversationPanel();

  const {
    data: meta,
    isLoading,
    error,
  } = useQuery({
    queryKey: sharedKeys.meta(fileId),
    queryFn: () => getSharedFileMeta(fileId),
    retry: false,
  });

  const preview = useQuery({
    queryKey: ["shared-file-preview", fileId],
    queryFn: () => fetchSharedPreviewBlob(fileId),
    enabled: Boolean(meta),
  });

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && navigate("/shared-with-me");
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [navigate]);

  const handleDownload = async () => {
    const toastId = toast.loading(`Downloading ${meta.file.name}…`);
    try {
      await downloadSharedFile(fileId, meta.file.name);
      toast.success("Download ready", { id: toastId });
    } catch (err) {
      toast.error(getErrorMessage(err, "Download failed"), { id: toastId });
    }
  };

  // withUploadToast reports progress, success and failure itself.
  const uploadVersion = useMutation({
    mutationFn: (file) =>
      withUploadToast(file.name, (onProgress) =>
        uploadSharedFileVersion({ fileId, file, onProgress }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sharedKeys.meta(fileId) });
      queryClient.invalidateQueries({
        queryKey: ["shared-file-preview", fileId],
      });
    },
  });

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <Spinner />
      </div>
    );
  }

  if (error || !meta) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50 p-4">
        <div className="w-full max-w-md space-y-4 rounded-2xl border border-slate-200 bg-slate-100 p-8 text-center">
          <FileText className="mx-auto h-10 w-10 text-slate-400" />
          <h1 className="text-lg font-semibold text-slate-800">
            This file isn’t available
          </h1>
          <p className="text-sm text-slate-500">
            {getErrorMessage(error, "Access may have been revoked.")}
          </p>
          <Button onClick={() => navigate("/shared-with-me")}>
            Back to Shared with me
          </Button>
        </div>
      </div>
    );
  }

  const { file, canDownload, canEdit, canChat } = meta;

  return (
    <div className="flex h-screen flex-col bg-slate-50">
      <header className="flex shrink-0 items-center gap-3 border-b border-slate-200 bg-slate-100 px-4 py-3">
        <button
          onClick={() => navigate("/shared-with-me")}
          aria-label="Back to Shared with me"
          className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <FileText className="h-5 w-5 shrink-0 text-slate-400" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-800">
            {file.name}
          </p>
          <p className="text-xs text-slate-400">
            {formatBytes(file.sizeBytes)}
          </p>
        </div>
        {canDownload && (
          <button
            onClick={handleDownload}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-200"
          >
            <Download className="h-4 w-4" />
            Download
          </button>
        )}
        {canEdit && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadVersion.mutate(file);
                e.target.value = "";
              }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadVersion.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-200 disabled:opacity-50"
            >
              {uploadVersion.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              Upload new version
            </button>
          </>
        )}
        {canChat && (
          <button
            onClick={() => setConversationOpen((o) => !o)}
            aria-label="Toggle comments and chat"
            aria-pressed={conversationOpen}
            className={`inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm font-medium transition-colors ${
              conversationOpen
                ? "bg-slate-200 text-slate-800"
                : "text-slate-700 hover:bg-slate-200"
            }`}
          >
            <MessageSquare className="h-4 w-4" />
            <span className="hidden sm:inline">Comments</span>
          </button>
        )}
        <button
          onClick={() => navigate("/shared-with-me")}
          aria-label="Close viewer"
          className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700"
        >
          <X className="h-5 w-5" />
        </button>
      </header>

      <div className="flex min-h-0 flex-1">
        <main className="scrollbar-thin min-h-0 flex-1 overflow-auto">
          {preview.isLoading ? (
            <div className="flex h-full items-center justify-center text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : preview.isError ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-slate-400">
              <FileText className="h-10 w-10" />
              <p className="text-sm">Couldn’t load a preview.</p>
            </div>
          ) : (
            <InlinePreview
              mimeType={file.mimeType}
              name={file.name}
              blob={preview.data}
              allowCopy={canEdit || canDownload}
            />
          )}
        </main>
        {canChat && conversationOpen && (
          <SharedConversationSidebar fileId={fileId} />
        )}
      </div>
    </div>
  );
}
